/**
 * Add Phone Modal Component
 *
 * Modal for adding a single phone number manually.
 * After adding, offers verification for live carrier testing.
 */

import { useState } from 'react';
import { Logger, LogCategory } from '@/utils/logger';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { usePhoneStore, useCallGroups } from '@/stores/phoneStore';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import { formatPhoneNumber, validatePhoneNumber } from '@/utils';
import { VerifyNumberModal } from './VerifyNumberModal';
import { Phone, Plus, ShieldCheck, SkipForward, AlertTriangle } from 'lucide-react';

interface AddPhoneModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  phoneNumber: string;
  phoneNumberGroup: string;
  description: string;
  internalId: string;
}

const initialFormData: FormData = {
  phoneNumber: '',
  phoneNumberGroup: '',
  description: '',
  internalId: '',
};

export function AddPhoneModal({ isOpen, onClose }: AddPhoneModalProps) {
  const { success: showSuccess, error: showError } = useToast();
  const { addPhones, fetchPhones } = usePhoneStore();
  const company = useAuthStore((state) => state.company);
  const callGroups = useCallGroups();

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState<'form' | 'verify'>('form');
  const [addedNumber, setAddedNumber] = useState('');
  const [showVerifyModal, setShowVerifyModal] = useState(false);

  const formatPhone = (value: string): string => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleChange = (field: keyof FormData, value: string) => {
    const formatted = field === 'phoneNumber' ? formatPhone(value) : value;
    setFormData((prev) => ({ ...prev, [field]: formatted }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else {
      const cleanedNumber = formData.phoneNumber.replace(/\D/g, '');
      if (!validatePhoneNumber(cleanedNumber)) {
        newErrors.phoneNumber = 'Please enter a valid 10-digit phone number';
      }
    }

    if (!formData.phoneNumberGroup) {
      newErrors.phoneNumberGroup = 'Group is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    const cleanedNumber = formData.phoneNumber.replace(/\D/g, '');

    try {
      // Check if this number already exists (possibly archived)
      if (company?.id) {
        const { data: existing } = await (supabase.from('phone_numbers') as any)
          .select('id, is_archived')
          .eq('company_id', company.id)
          .eq('number', cleanedNumber)
          .single();

        if (existing) {
          if (existing.is_archived) {
            // Unarchive the existing number instead of inserting
            await (supabase.from('phone_numbers') as any)
              .update({
                is_archived: false,
                flag_status: 'clean',
                reputation_score: 100,
                reputation_sources: [],
                description: formData.description.trim() || null,
                internal_id: formData.internalId.trim() || null,
              })
              .eq('id', existing.id);

            await fetchPhones();
            showSuccess('Phone restored', `${formatPhoneNumber(cleanedNumber)} has been restored from archive.`);
            setAddedNumber(cleanedNumber);
            setStep('verify');
            setIsSubmitting(false);
            return;
          } else {
            showError('Already exists', `${formatPhoneNumber(cleanedNumber)} is already in your phone list.`);
            setIsSubmitting(false);
            return;
          }
        }
      }

      // Add new phone
      await addPhones([
        {
          number: cleanedNumber,
          formattedNumber: formatPhoneNumber(cleanedNumber),
          callGroup: formData.phoneNumberGroup || null,
          description: formData.description.trim() || undefined,
          internalId: formData.internalId.trim() || undefined,
          flagStatus: 'clean',
          reputationScore: 100,
          reputationSources: [],
        },
      ]);

      showSuccess('Phone added', `${formatPhoneNumber(cleanedNumber)} has been added.`);
      setAddedNumber(cleanedNumber);
      setStep('verify');
    } catch (error) {
      Logger.error(LogCategory.DATA, 'Failed to add phone number', 'AddPhoneModal', undefined, error);
      showError('Error', 'Failed to add phone number. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    setStep('form');
    setAddedNumber('');
    onClose();
  };

  const phoneNumberGroupOptions = [
    { value: '', label: 'Select a group...' },
    ...callGroups.map((group) => ({
      value: group.name,
      label: group.name,
    })),
  ];

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={step === 'form' ? 'Add Phone Number' : 'Verify for Live Testing'}
        size="md"
      >
        {step === 'form' ? (
          <div className="space-y-4">
            <Input
              label="Phone Number"
              placeholder="(555) 123-4567"
              value={formData.phoneNumber}
              onChange={(e) => handleChange('phoneNumber', e.target.value)}
              error={errors.phoneNumber}
              leftIcon={<Phone className="h-4 w-4" />}
              required
            />

            <Select
              label="Group"
              value={formData.phoneNumberGroup}
              onChange={(e) => handleChange('phoneNumberGroup', e.target.value)}
              options={phoneNumberGroupOptions}
              error={errors.phoneNumberGroup}
              required
            />

            <Input
              label="Internal ID"
              placeholder="e.g., SALES-001"
              value={formData.internalId}
              onChange={(e) => handleChange('internalId', e.target.value)}
              helperText="Optional - Your internal reference number"
            />

            <Input
              label="Description"
              placeholder="e.g., Main sales line"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              helperText="Optional - Notes about this number"
            />

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                isLoading={isSubmitting}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Add Number
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-center py-2">
              <div className="mx-auto h-12 w-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                <Phone className="h-6 w-6 text-green-600" />
              </div>
              <p className="font-medium text-slate-900">
                {formatPhoneNumber(addedNumber)} added successfully!
              </p>
            </div>

            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Verify for live carrier testing</p>
                  <p className="text-amber-700 mt-1">
                    Unverified numbers can be checked via API sources (Nomorobo, FTC, etc.), but
                    live carrier testing (AT&T, T-Mobile, Verizon call screens) requires number verification.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button
                variant="outline"
                onClick={handleClose}
                leftIcon={<SkipForward className="h-4 w-4" />}
              >
                Skip for now
              </Button>
              <Button
                onClick={() => { setShowVerifyModal(true); }}
                leftIcon={<ShieldCheck className="h-4 w-4" />}
              >
                Verify Now
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <VerifyNumberModal
        isOpen={showVerifyModal}
        onClose={() => { setShowVerifyModal(false); handleClose(); }}
        phoneNumber={formatPhone(addedNumber)}
      />
    </>
  );
}
